import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MediaCard from "./MediaCard";
import Pagination from "./Pagination";
import EmptyState from "./EmptyState";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  filterMedia,
  getAllGenres,
  getAllRegions,
  getYearBuckets,
  sortMedia,
} from "@/lib/media";
import type { SortKey } from "@/types/media";

interface CatalogPageProps {
  type: "movie" | "tv";
  heading: string;
  intro: string;
  breadcrumb: string;
  showRegion?: boolean;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
}

const PER_PAGE = 8;

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新" },
  { key: "rating", label: "评分" },
  { key: "hot", label: "热度" },
];

export default function CatalogPage({
  type,
  heading,
  intro,
  breadcrumb,
  showRegion = false,
  metaTitle,
  metaDescription,
  metaKeywords,
}: CatalogPageProps) {
  const isMobile = useIsMobile();
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [sortKey, setSortKey] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  useDocumentMeta(metaTitle, metaDescription, metaKeywords);

  const genres = useMemo(() => getAllGenres(type), [type]);
  const regions = useMemo(() => getAllRegions(type), [type]);
  const yearBuckets = useMemo(() => getYearBuckets(type), [type]);

  const activeBucket = yearBuckets.find((bucket) => bucket.label === selectedYear);

  const results = useMemo(() => {
    const filtered = filterMedia({
      type,
      genres: selectedGenres,
      region: showRegion ? selectedRegion : undefined,
      yearTest: activeBucket?.test,
    });
    return sortMedia(filtered, sortKey);
  }, [type, selectedGenres, selectedRegion, showRegion, activeBucket, sortKey]);

  useEffect(() => {
    setPage(1);
  }, [selectedGenres, selectedRegion, selectedYear, sortKey]);

  const totalPages = Math.max(1, Math.ceil(results.length / PER_PAGE));
  const visible = isMobile
    ? results.slice(0, page * PER_PAGE)
    : results.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const hasFilters = selectedGenres.length > 0 || selectedRegion !== "" || selectedYear !== "";

  const toggleGenre = (genre: string) => {
    setSelectedGenres((prev) =>
      prev.includes(genre) ? prev.filter((item) => item !== genre) : [...prev, genre],
    );
  };

  const clearFilters = () => {
    setSelectedGenres([]);
    setSelectedRegion("");
    setSelectedYear("");
    setSortKey("latest");
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-12">
      <nav aria-label="面包屑" className="mb-4 flex items-center gap-1.5 text-sm text-foreground-400">
        <Link to="/" className="transition-colors hover:text-accent-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-200">{breadcrumb}</span>
      </nav>

      <header className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-4xl">{heading}</h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-300 md:text-base">{intro}</p>
      </header>

      <section
        className="sticky top-16 z-30 -mx-4 mb-8 space-y-3 border-b border-background-800 bg-background-950/95 px-4 py-4 backdrop-blur md:top-20 md:-mx-8 md:px-8"
        aria-label="筛选与排序"
      >
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          <span className="shrink-0 text-sm font-medium text-secondary-300">类型</span>
          {genres.map((genre) => {
            const active = selectedGenres.includes(genre);
            return (
              <button
                key={genre}
                type="button"
                onClick={() => toggleGenre(genre)}
                aria-pressed={active}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm whitespace-nowrap transition-colors ${
                  active
                    ? "bg-primary-500 font-medium text-foreground-50"
                    : "bg-secondary-100/10 text-secondary-200 hover:bg-secondary-100/20"
                }`}
              >
                {genre}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <span className="shrink-0 text-sm font-medium text-secondary-300">年份</span>
            <button
              type="button"
              onClick={() => setSelectedYear("")}
              className={`shrink-0 rounded-full px-3 py-1 text-sm whitespace-nowrap transition-colors ${
                selectedYear === ""
                  ? "bg-accent-500 font-medium text-background-950"
                  : "bg-background-800 text-foreground-300 hover:text-accent-400"
              }`}
            >
              全部
            </button>
            {yearBuckets.map((bucket) => (
              <button
                key={bucket.label}
                type="button"
                onClick={() => setSelectedYear(bucket.label)}
                className={`shrink-0 rounded-full px-3 py-1 text-sm whitespace-nowrap transition-colors ${
                  selectedYear === bucket.label
                    ? "bg-accent-500 font-medium text-background-950"
                    : "bg-background-800 text-foreground-300 hover:text-accent-400"
                }`}
              >
                {bucket.label}
              </button>
            ))}

            {showRegion && (
              <>
                <span className="ml-2 shrink-0 text-sm font-medium text-secondary-300">地区</span>
                <button
                  type="button"
                  onClick={() => setSelectedRegion("")}
                  className={`shrink-0 rounded-full px-3 py-1 text-sm whitespace-nowrap transition-colors ${
                    selectedRegion === ""
                      ? "bg-accent-500 font-medium text-background-950"
                      : "bg-background-800 text-foreground-300 hover:text-accent-400"
                  }`}
                >
                  全部
                </button>
                {regions.map((region) => (
                  <button
                    key={region}
                    type="button"
                    onClick={() => setSelectedRegion(region)}
                    className={`shrink-0 rounded-full px-3 py-1 text-sm whitespace-nowrap transition-colors ${
                      selectedRegion === region
                        ? "bg-accent-500 font-medium text-background-950"
                        : "bg-background-800 text-foreground-300 hover:text-accent-400"
                    }`}
                  >
                    {region}
                  </button>
                ))}
              </>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <div className="flex items-center gap-1 rounded-full bg-background-800 p-1">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSortKey(option.key)}
                  className={`rounded-full px-3 py-1 text-sm whitespace-nowrap transition-colors ${
                    sortKey === option.key
                      ? "bg-primary-500 font-medium text-foreground-50"
                      : "text-foreground-300 hover:text-accent-400"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="flex items-center gap-1 rounded-full border border-background-700 px-3 py-1.5 text-sm text-foreground-300 whitespace-nowrap transition-colors hover:border-accent-400 hover:text-accent-400"
              >
                <i className="ri-refresh-line"></i>
                清除筛选
              </button>
            )}
          </div>
        </div>
      </section>

      {results.length === 0 ? (
        <EmptyState
          icon="ri-movie-line"
          title={`抱歉，没有符合条件的${type === "tv" ? "电视剧" : "电影"}`}
          description="尝试调整类型、年份或地区筛选，或清除全部筛选条件查看全部内容。"
          action={
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-950 whitespace-nowrap transition-colors hover:bg-accent-400"
            >
              清除全部筛选
            </button>
          }
        />
      ) : (
        <>
          <p className="mb-4 text-sm text-foreground-400">
            共找到 <span className="font-semibold text-accent-400">{results.length}</span> 部内容
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
            {visible.map((item) => (
              <MediaCard key={item.id} item={item} className="animate-fade-up" />
            ))}
          </div>

          {!isMobile && <Pagination page={page} totalPages={totalPages} onChange={setPage} />}

          {isMobile && page < totalPages && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setPage((prev) => prev + 1)}
                className="flex items-center gap-2 rounded-full border border-background-700 px-6 py-2.5 text-sm text-foreground-100 whitespace-nowrap transition-colors hover:border-accent-400 hover:text-accent-400"
              >
                <i className="ri-add-line"></i>
                加载更多
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}